import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useRef, useId } from "react";

type Props = {
  flip?: boolean;
  className?: string;
};

/**
 * SVG divider whose blob morphs as the section scrolls past.
 * Uses a single path with two animated control points. Very cheap.
 */
export const LiquidDivider = ({ flip = false, className = "" }: Props) => {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const uid = useId();
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });
  const c1 = useTransform(scrollYProgress, [0, 1], reduced ? [40, 40] : [20, 60]);
  const c2 = useTransform(scrollYProgress, [0, 1], reduced ? [40, 40] : [60, 20]);
  const d = useTransform([c1, c2], ([a, b]: number[]) =>
    `M0,${flip ? 0 : 80} C360,${a} 1080,${b} 1440,${flip ? 0 : 80} L1440,${flip ? 80 : 0} L0,${flip ? 80 : 0} Z`
  );

  return (
    <div ref={ref} className={`relative w-full h-12 md:h-16 overflow-hidden ${className}`} aria-hidden>
      <svg
        viewBox="0 0 1440 80"
        preserveAspectRatio="none"
        className="absolute inset-0 size-full"
      >
        <defs>
          <linearGradient id={`lq-${uid}`} x1="0" x2="1" y1="0" y2="0">
            <stop offset="0%" stopColor="hsl(var(--sand))" />
            <stop offset="50%" stopColor="hsl(var(--cream))" />
            <stop offset="100%" stopColor="hsl(var(--sand))" />
          </linearGradient>
        </defs>
        <motion.path d={d} fill={`url(#lq-${uid})`} opacity={0.7} />
      </svg>
    </div>
  );
};
